import {
  Box,
  CloseIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  DataGrid,
  GridColDef,
  GridRowSelectionModel,
  useState,
  useMemo,
  useEffect,
  SearchBar,
  Typography,
  Tooltip,
} from "../../utils/globalImports";

interface Column {
  field: string;
  headerName: string;
}

interface Row {
  [key: string]: string | number;
}

interface CustomDataTableModalProps {
  rows: Row[];
  columns: Column[];
  heading: string;
  open: boolean;
  onClose: () => void;
  actionText?: "framework" | "feeStage";
}

const CustomDataTableModal: React.FC<CustomDataTableModalProps> = ({
  rows,
  columns,
  heading,
  open,
  onClose,
  actionText = "framework",
}) => {
  // const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
    }
  }, [open]);

  const rowsWithId = useMemo(
    () => rows.map((row, index) => ({ id: index, ...row })),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return rowsWithId;
    return rowsWithId.filter((row) =>
      columns.some((col) =>
        String(row[col.field] ?? "").toLowerCase().includes(q)
      )
    );
  }, [searchQuery, rowsWithId, columns]);

  

  const gridColumns: GridColDef[] = columns.map((col) => ({
    field: col.field,
    headerName: col.headerName,
    flex: 1,
    align: "left" as const,
    headerAlign: "left" as const,
    sortable: true,
  }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          position: "relative",
          color: "#1e66ab",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 500,
          fontSize: "24px",
        }}
      >
        {heading}
        <IconButton
          color="inherit"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 24,
            top: 16,
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "6px",
            color: "#1e66ab",
            "&:hover": { backgroundColor: "#f8fafc" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 3 }}>
        {actionText === "framework" ? (
          <Box sx={{ overflow: "auto", maxHeight: 300 }}>
            <Box
              component="table"
              sx={{ width: "100%", borderCollapse: "collapse" }}
            >
              <Box component="thead">
                <Box component="tr">
                  {columns.map((col) => (
                    <Box
                      component="th"
                      key={col.field}
                      sx={{
                        borderBottom: "1px solid #ddd",
                        px: 5,
                        py: 1,
                        textAlign: "left",
                        color: "#185f97",
                        fontSize: "16px",
                        fontWeight: 400,
                      }}
                    >
                      {col.headerName}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {rows.map((row, rowIndex) => (
                  <Box component="tr" key={rowIndex}>
                    {columns.map((col) => {
                      const value = row[col.field];
                      const isLongText = typeof value === 'string' && value.length > 60;
                      const displayText = isLongText ? value.slice(0, 45) + '...' : value;
                      const tooltipText = isLongText ? value.slice(0, 500) + (value.length > 500 ? '...' : '') : '';
                      return (
                        <Box
                          component="td"
                          key={col.field}
                          sx={{
                            borderBottom: "1px solid #eee",
                            px: 5,
                            py: 1,
                            color: "#185f97",
                            fontSize: "14px",
                            fontWeight: 400,
                            textAlign: "left",
                          }}
                        >
                          {isLongText ? (
                            <Tooltip title={tooltipText}>
                              <span>{displayText}</span>
                            </Tooltip>
                          ) : (
                            value
                          )}
                        </Box>
                      );

                    })}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

        ) : (

          <>
            {/* Search box */}
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderBottom: "none",
                borderRadius: "10px 10px 0 0",
                overflow: "hidden",
              }}
            >
              <Box sx={{ px: 2.5, py: 1.5, backgroundColor: "#fff", width: "50%" }}>
                <SearchBar
                  placeholder="Search by reference no. or description..."
                  onQueryChange={setSearchQuery}
                />
              </Box>
            </Box>

            {/* DataGrid */}
            <Box sx={{ height: 400, width: "100%" }}>
              <DataGrid
                rows={filteredRows}
                columns={gridColumns}
                getRowId={(row) => row.id}
                keepNonExistentRowsSelected
                initialState={{
                  pagination: { paginationModel: { pageSize: 5 } },
                }}
                pageSizeOptions={[5, 10, 25]}
                disableRowSelectionOnClick={false}
                slots={{

                  noRowsOverlay: () => (
                    <Box sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      width: "100%"
                    }}>
                      <Typography sx={{ color: "#1e66ab", fontSize: "14px" }}>
                        No data found
                      </Typography>
                    </Box>
                  ),
                }}
                sx={{

                  "& .MuiDataGrid-columnHeaders": {
                    borderBottom: "2px solid #e0e0e0",
                    backgroundColor: "#f5f8ff",
                  },

                  "& .MuiDataGrid-columnHeader": {
                    padding: "0 16px",
                    fontWeight: 700,
                    fontSize: "16px",
                    color: "#1e66ab",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                  },

                  "& .MuiDataGrid-columnHeaderTitleContainer": {
                    justifyContent: "flex-start",
                    padding: 0,
                  },

                  "& .MuiDataGrid-columnHeaderTitle": {
                    textAlign: "left",
                    width: "fit-content",
                    letterSpacing: "0.2px",
                  },

                  "& .MuiDataGrid-columnSeparator": {
                    display: "none !important",
                  },

                  "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                    outline: "none",
                  },

                  /* ── Cells ── */
                  "& .MuiDataGrid-cell": {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    padding: "0 16px",
                    whiteSpace: "normal",
                    color: "#1e66ab",
                    fontSize: "14px",
                    borderBottom: "1px solid #f0f0f0",
                  },

                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },

                  /* ── Rows ── */
                  "& .MuiDataGrid-row": {
                    borderLeft: "4px solid transparent",
                    backgroundColor: "white",
                    boxSizing: "border-box",
                    cursor: "pointer",
                    transition: "background-color 0.15s ease",
                  },

                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#f0f4ff",
                    borderLeft: "4px solid #1e66ab33",
                  },

                  /* ── Selected row ── */
                  "& .MuiDataGrid-row.Mui-selected": {
                    backgroundColor: "rgba(150, 193, 33, 0.10) !important",
                    borderLeft: "4px solid #96c121",
                  },

                  "& .MuiDataGrid-row.Mui-selected:hover": {
                    backgroundColor: "rgba(150, 193, 33, 0.18) !important",
                  },

                  /* ── Footer ── */
                  "& .MuiDataGrid-footerContainer": {
                    color: "#1e66ab",
                    borderTop: "2px solid #e0e0e0",
                    backgroundColor: "#f5f8ff",
                    minHeight: "52px",
                  },

                }}
              />
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomDataTableModal;